// Wins & worries — the biggest CPL improvements and declines vs the comparison
// range, volume-guarded (the client-good-news finder as much as the worry list).

import { Link } from 'react-router-dom';
import { AccountLinkIcons } from '../../components/ui/AccountLinkIcons';
import type { AccountLinkKey } from '../../lib/accountLinks';
import { cn } from '../../lib/utils';
import type { AccountCplChange, AccountLeadTransition } from '../../lib/advertisingHome/cplChange';
import { formatReportingAmount } from '../../lib/reportingMoney';
import { PanelTitle } from '../reporting/PanelTitle';
import { accountHref } from './deepLinks';

function ChangeList({
  title,
  entries,
  nameOf,
  tone,
  emptyText,
  linkKeys,
  transitions = [],
}: {
  /** Zero-lead transitions (noLeads.ts) — listed FIRST: never dropped from a ranking. */
  transitions?: AccountLeadTransition[];
  linkKeys?: AccountLinkKey[];
  title: string;
  entries: AccountCplChange[];
  nameOf: (accountId: string) => string;
  tone: 'win' | 'worry';
  emptyText: string;
}): React.JSX.Element {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <PanelTitle className="mb-2 block">{title}</PanelTitle>
      {entries.length === 0 && transitions.length === 0 ? (
        <p className="text-sm text-muted-foreground">{emptyText}</p>
      ) : (
        // Ten entries each: the two lists stay side by side and SCROLL
        // INTERNALLY rather than stretching the page past the table.
        <ul className="scroll-subtle max-h-64 space-y-1.5 overflow-y-auto pr-1">
          {transitions.map((t) => (
            <li
              key={`t-${t.accountId}`}
              className="flex items-baseline justify-between gap-2 text-sm"
              data-testid={`transition-${t.kind}`}
            >
              <Link
                to={accountHref(t.accountId)}
                className="min-w-0 truncate font-medium hover:underline"
              >
                {nameOf(t.accountId)}
              </Link>
              <span
                className={cn(
                  'shrink-0 text-xs font-semibold tabular-nums',
                  t.kind === 'leads_gone' ? 'text-red-600' : 'text-kyck-600',
                )}
              >
                {t.kind === 'leads_gone'
                  ? `No leads (was ${t.leadsPrevious}) · ${formatReportingAmount(t.spendCurrent, t.currency)} spent`
                  : `Leads back (${t.leadsCurrent})`}
              </span>
            </li>
          ))}
          {entries.map((e) => (
            <li key={e.accountId} className="flex items-baseline justify-between gap-2 text-sm">
              <span className="flex min-w-0 items-center gap-1">
                <Link
                  to={accountHref(e.accountId)}
                  className="min-w-0 truncate font-medium hover:underline"
                >
                  {nameOf(e.accountId)}
                </Link>
                {linkKeys && (
                  <AccountLinkIcons accountId={e.accountId} className="shrink-0" only={linkKeys} />
                )}
              </span>
              <span className="flex shrink-0 items-baseline gap-2 tabular-nums">
                <span className="text-xs text-muted-foreground">
                  {formatReportingAmount(e.cplPrevious, e.currency)} →{' '}
                  {formatReportingAmount(e.cplCurrent, e.currency)}
                </span>
                <span
                  className={cn(
                    'font-semibold',
                    tone === 'win' ? 'text-kyck-600' : 'text-red-600',
                  )}
                >
                  {e.changePct > 0 ? '+' : ''}
                  {Math.round(e.changePct)}%
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function WinsWorries({
  wins,
  worries,
  leadsBack = [],
  leadsGone = [],
  nameOf,
  linkKeys,
}: {
  leadsBack?: AccountLeadTransition[];
  leadsGone?: AccountLeadTransition[];
  linkKeys?: AccountLinkKey[];
  wins: AccountCplChange[];
  worries: AccountCplChange[];
  nameOf: (accountId: string) => string;
}): React.JSX.Element {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <ChangeList
        title="Wins — biggest CPL improvements"
        entries={wins}
        transitions={leadsBack}
        nameOf={nameOf}
        linkKeys={linkKeys}
        tone="win"
        emptyText="No qualifying improvements in this range."
      />
      <ChangeList
        title="Worries — biggest CPL declines"
        entries={worries}
        transitions={leadsGone}
        nameOf={nameOf}
        linkKeys={linkKeys}
        tone="worry"
        emptyText="No qualifying declines in this range."
      />
    </div>
  );
}
